import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl, uploadFile } from "@/lib/api";
import { LEVELS } from "@/components/shared";
import {
  FileText, Film, Music, File as FileIcon, Star, Trash2, ExternalLink,
  CheckCircle2, AlertTriangle, MessageSquare, Loader2, Mic, Square, X, History, ChevronDown, Upload,
} from "lucide-react";

const STATUS = {
  validated: { label: "Validée", cls: "text-emerald-700 bg-emerald-100", Icon: CheckCircle2 },
  to_revise: { label: "À revoir", cls: "text-amber-700 bg-amber-100", Icon: AlertTriangle },
};

function ReviewHistory({ history }) {
  const [open, setOpen] = useState(false);
  if (!history?.length) return null;
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="review-history-toggle"
        className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-700">
        <History className="h-3.5 w-3.5" /> Historique des évaluations ({history.length})
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid="review-history-list">
          {history.map((h, i) => {
            const st = STATUS[h.status];
            const key = h.reviewed_at || `history-${i}`;
            return (
              <div key={key} className="rounded-xl bg-slate-50 border border-slate-200 p-2.5" data-testid={`review-history-item-${i}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {st && <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold px-1.5 py-0.5 rounded-full ${st.cls}`}><st.Icon className="h-3 w-3" /> {st.label}</span>}
                  {h.level && <span className="text-[10px] font-extrabold text-slate-400">Niveau {LEVELS[h.level - 1]?.code}</span>}
                  <span className="text-[10px] font-bold text-slate-400 ml-auto">{(h.reviewed_at || "").slice(0, 10)}</span>
                </div>
                {h.comment && <p className="text-xs text-slate-600">{h.comment}</p>}
                {h.comment_audio && <audio src={fileUrl(h.comment_audio.id)} controls className="w-full h-8 mt-1.5" />}
                {h.comment_file && (
                  <a href={fileUrl(h.comment_file.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-700 hover:underline mt-1.5">
                    <FileText className="h-3.5 w-3.5" /> {h.comment_file.name} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilePreview({ f }) {
  const url = fileUrl(f.id);
  const ct = f.content_type || "";
  if (ct.startsWith("image/")) {
    return (
      <div>
        <a href={url} target="_blank" rel="noreferrer" className="block"><img src={url} alt={f.name} className="h-24 w-full object-cover rounded-xl border border-slate-200" /></a>
        {f.description && <p className="text-xs font-medium text-slate-500 mt-1 line-clamp-2" data-testid={`proof-photo-description-${f.id}`}>{f.description}</p>}
      </div>
    );
  }
  if (ct.startsWith("video/")) {
    return <video src={url} controls className="h-32 w-full object-cover rounded-xl border border-slate-200 bg-black" />;
  }
  if (ct.startsWith("audio/")) {
    return <div className="rounded-xl border border-slate-200 p-3 bg-slate-50"><div className="flex items-center gap-2 text-slate-600 mb-2"><Music className="h-4 w-4" /><span className="text-xs font-bold truncate">{f.name}</span></div><audio src={url} controls className="w-full" /></div>;
  }
  const Icon = ct.includes("pdf") ? FileText : FileIcon;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl border border-slate-200 p-3 bg-slate-50 hover:bg-slate-100">
      <Icon className="h-5 w-5 text-slate-500 shrink-0" />
      <span className="text-xs font-bold text-slate-700 truncate flex-1">{f.name}</span>
      <ExternalLink className="h-4 w-4 text-slate-400" />
    </a>
  );
}

function ReviewControls({ proof, onChanged }) {
  const [status, setStatus] = useState(proof.validation_status === "pending" ? "" : proof.validation_status || "");
  const [comment, setComment] = useState(proof.teacher_comment || "");
  const [level, setLevel] = useState(proof.selfeval_form?.level || 1);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioRemoved, setAudioRemoved] = useState(false);
  const [correctionFile, setCorrectionFile] = useState(null);
  const [correctionFileRemoved, setCorrectionFileRemoved] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const existingAudio = !audioRemoved && !audioBlob ? proof.teacher_comment_audio : null;
  const existingCorrectionFile = !correctionFileRemoved && !correctionFile ? proof.teacher_comment_file : null;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Micro inaccessible. Vérifie les autorisations du navigateur.");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const clearAudio = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioRemoved(true);
  };

  const clearCorrectionFile = () => {
    setCorrectionFile(null);
    setCorrectionFileRemoved(true);
  };

  const save = async () => {
    if (!status) { toast.error("Choisis un statut (Validée ou À revoir)."); return; }
    setBusy(true);
    try {
      let comment_audio_id = proof.teacher_comment_audio?.id || null;
      if (audioBlob) {
        const file = new File([audioBlob], `commentaire-${proof.id}.webm`, { type: audioBlob.type || "audio/webm" });
        const up = await uploadFile(file);
        comment_audio_id = up.id;
      } else if (audioRemoved) {
        comment_audio_id = null;
      }
      let comment_file_id = proof.teacher_comment_file?.id || null;
      if (correctionFile) {
        const up = await uploadFile(correctionFile);
        comment_file_id = up.id;
      } else if (correctionFileRemoved) {
        comment_file_id = null;
      }
      await api.patch(`/admin/proofs/${proof.id}/review`, { status, comment, level, comment_audio_id, comment_file_id });
      toast.success("Évaluation enregistrée ✓");
      onChanged && onChanged();
    } catch {
      toast.error("Enregistrement impossible");
    } finally { setBusy(false); }
  };

  const statusBtn = (val, label, activeCls) =>
    `flex-1 rounded-xl border-2 px-3 py-2 text-sm font-bold transition-colors ${
      status === val ? activeCls : "border-slate-200 text-slate-500 hover:bg-slate-50"
    }`;

  return (
    <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-3" data-testid={`review-controls-${proof.id}`}>
      <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-500">Évaluation du professeur</p>

      <div className="flex gap-2">
        <button type="button" onClick={() => setStatus("validated")} data-testid={`review-validate-${proof.id}`}
          className={statusBtn("validated", "Validée", "border-emerald-400 bg-emerald-100 text-emerald-700")}>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Validée</span>
        </button>
        <button type="button" onClick={() => setStatus("to_revise")} data-testid={`review-revise-${proof.id}`}
          className={statusBtn("to_revise", "À revoir", "border-amber-400 bg-amber-100 text-amber-700")}>
          <span className="inline-flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> À revoir</span>
        </button>
      </div>

      <div>
        <p className="text-[11px] font-bold text-slate-500 mb-1.5">Niveau validé pour {proof.competency_code}</p>
        <div className="grid grid-cols-4 gap-1.5">
          {LEVELS.map((l) => (
            <button key={l.n} type="button" onClick={() => setLevel(l.n)} data-testid={`review-level-${proof.id}-${l.code}`}
              className={`rounded-xl border-2 px-1 py-2 text-center transition-colors ${
                level === l.n ? "border-orange-400 bg-orange-100 text-orange-700" : "border-slate-200 text-slate-400 hover:bg-slate-100"
              }`}>
              <span className="block text-[10px] font-extrabold">{l.code}</span>
              <span className="block text-[11px] font-bold">{l.label}</span>
            </button>
          ))}
        </div>
      </div>

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Commentaire détaillé pour l'élève (facultatif)…"
        rows={3}
        data-testid={`review-comment-${proof.id}`}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
      />

      <div>
        <p className="text-[11px] font-bold text-slate-500 mb-1.5">Commentaire audio (facultatif)</p>
        {existingAudio && (
          <div className="flex items-center gap-2 mb-2 rounded-xl bg-white border border-slate-200 p-2" data-testid={`review-audio-existing-${proof.id}`}>
            <audio src={fileUrl(existingAudio.id)} controls className="flex-1 h-9" />
            <button type="button" onClick={clearAudio} data-testid={`review-audio-remove-${proof.id}`} className="h-7 w-7 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center shrink-0"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
        {audioBlob && (
          <div className="flex items-center gap-2 mb-2 rounded-xl bg-white border border-slate-200 p-2" data-testid={`review-audio-preview-${proof.id}`}>
            <audio src={audioUrl} controls className="flex-1 h-9" />
            <button type="button" onClick={clearAudio} data-testid={`review-audio-discard-${proof.id}`} className="h-7 w-7 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center shrink-0"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
        {!recording ? (
          <button type="button" onClick={startRecording} data-testid={`review-audio-start-${proof.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-200 hover:border-orange-300 text-slate-600 hover:text-orange-600 px-4 py-2 text-xs font-bold transition-colors">
            <Mic className="h-3.5 w-3.5" /> {existingAudio || audioBlob ? "Remplacer l'audio" : "Enregistrer un commentaire audio"}
          </button>
        ) : (
          <button type="button" onClick={stopRecording} data-testid={`review-audio-stop-${proof.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-red-400 bg-red-50 text-red-600 px-4 py-2 text-xs font-bold animate-pulse">
            <Square className="h-3.5 w-3.5 fill-red-600" /> Arrêter l'enregistrement
          </button>
        )}
      </div>

      <div>
        <p className="text-[11px] font-bold text-slate-500 mb-1.5">Document corrigé à renvoyer à l'élève (facultatif)</p>
        {existingCorrectionFile && (
          <div className="flex items-center gap-2 mb-2 rounded-xl bg-white border border-slate-200 p-2" data-testid={`review-file-existing-${proof.id}`}>
            <FileText className="h-4 w-4 text-orange-500 shrink-0" />
            <a href={fileUrl(existingCorrectionFile.id)} target="_blank" rel="noreferrer" className="text-xs font-bold text-slate-700 truncate flex-1 hover:underline">{existingCorrectionFile.name}</a>
            <button type="button" onClick={clearCorrectionFile} data-testid={`review-file-remove-${proof.id}`} className="h-7 w-7 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center shrink-0"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
        {correctionFile && (
          <div className="flex items-center gap-2 mb-2 rounded-xl bg-white border border-slate-200 p-2" data-testid={`review-file-preview-${proof.id}`}>
            <FileText className="h-4 w-4 text-orange-500 shrink-0" />
            <span className="text-xs font-bold text-slate-700 truncate flex-1">{correctionFile.name}</span>
            <button type="button" onClick={clearCorrectionFile} data-testid={`review-file-discard-${proof.id}`} className="h-7 w-7 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center shrink-0"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
        <label data-testid={`review-file-input-${proof.id}`} className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-200 hover:border-orange-300 text-slate-600 hover:text-orange-600 px-4 py-2 text-xs font-bold transition-colors cursor-pointer">
          <Upload className="h-3.5 w-3.5" /> {existingCorrectionFile || correctionFile ? "Remplacer le document" : "Joindre un document corrigé"}
          <input type="file" className="hidden" onChange={(e) => { setCorrectionFile(e.target.files[0] || null); setCorrectionFileRemoved(false); }} />
        </label>
      </div>

      <button onClick={save} disabled={busy} data-testid={`review-save-${proof.id}`}
        className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white rounded-full px-4 py-2.5 font-bold text-sm transition-colors">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Enregistrer l'évaluation
      </button>
    </div>
  );
}

export default function ProofCard({ proof, onChanged, readOnly = false, reviewMode = false }) {
  const toggleSignif = async () => {
    try {
      await api.patch(`/proofs/${proof.id}/significative`, { significative: !proof.significative });
      toast.success(!proof.significative ? "Ajoutée aux compétences significatives ★" : "Retirée des compétences significatives");
      onChanged && onChanged();
    } catch {
      toast.error("Action impossible");
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/proofs/${proof.id}`);
      toast.success("Preuve supprimée");
      onChanged && onChanged();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const st = STATUS[proof.validation_status];

  return (
    <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-4" data-testid={`proof-card-${proof.id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-[11px] font-extrabold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{proof.competency_code}</span>
        {proof.sub_seance_title && (
          <span className="text-[11px] font-extrabold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full" data-testid={`proof-substep-badge-${proof.id}`}>
            Étape : {proof.sub_seance_title}
          </span>
        )}
        {proof.significative && (
          <span className="inline-flex items-center gap-1 text-[11px] font-extrabold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"><Star className="h-3 w-3 fill-amber-500 text-amber-500" /> Significative</span>
        )}
        {st && (
          <span data-testid={`proof-status-${proof.id}`} className={`inline-flex items-center gap-1 text-[11px] font-extrabold px-2 py-0.5 rounded-full ${st.cls}`}>
            <st.Icon className="h-3 w-3" /> {st.label}
          </span>
        )}
        <span className="text-[11px] font-bold text-slate-400 ml-auto">{(proof.created_at || "").slice(0, 10)}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {proof.files.map((f) => <FilePreview key={f.id} f={f} />)}
      </div>

      {/* self-eval */}
      <div className="mt-3 rounded-xl bg-sky-50 border border-sky-100 p-3">
        <p className="text-[11px] font-extrabold uppercase tracking-wide text-sky-700 mb-1">Autoévaluation</p>
        {proof.selfeval_type === "form" && proof.selfeval_form ? (
          <div className="text-sm text-slate-700 space-y-0.5">
            <p className="font-bold">Niveau auto-positionné : {LEVELS[proof.selfeval_form.level - 1]?.label}</p>
            <p><span className="font-bold">Points forts :</span> {proof.selfeval_form.points_forts}</p>
            <p><span className="font-bold">Axes de progrès :</span> {proof.selfeval_form.axes}</p>
          </div>
        ) : proof.selfeval_file ? (
          <a href={fileUrl(proof.selfeval_file.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-sky-700 hover:underline">
            <FileText className="h-4 w-4" /> {proof.selfeval_file.name} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : (
          <p className="text-sm text-slate-400">—</p>
        )}
      </div>

      {/* teacher comment (read-only display for everyone) */}
      {(proof.teacher_comment || proof.teacher_comment_audio || proof.teacher_comment_file) && (
        <div className="mt-3 rounded-xl bg-orange-50 border border-orange-100 p-3" data-testid={`proof-comment-${proof.id}`}>
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-orange-700 mb-1 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Commentaire du professeur</p>
          {proof.teacher_comment && <p className="text-sm text-slate-700">{proof.teacher_comment}</p>}
          {proof.teacher_comment_audio && (
            <audio src={fileUrl(proof.teacher_comment_audio.id)} controls className="w-full h-9 mt-2" data-testid={`proof-comment-audio-${proof.id}`} />
          )}
          {proof.teacher_comment_file && (
            <a href={fileUrl(proof.teacher_comment_file.id)} target="_blank" rel="noreferrer" data-testid={`proof-comment-file-${proof.id}`}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-orange-700 hover:underline mt-2">
              <FileText className="h-4 w-4" /> {proof.teacher_comment_file.name} (document corrigé) <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          <ReviewHistory history={proof.review_history} />
        </div>
      )}

      {reviewMode && <ReviewControls proof={proof} onChanged={onChanged} />}

      {!readOnly && !reviewMode && (
        <div className="flex items-center gap-2 mt-3">
          <button onClick={toggleSignif} data-testid={`proof-signif-${proof.id}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${proof.significative ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            <Star className={`h-3.5 w-3.5 ${proof.significative ? "fill-amber-500 text-amber-500" : ""}`} /> {proof.significative ? "Significative" : "Marquer significative"}
          </button>
          <button onClick={remove} data-testid={`proof-delete-${proof.id}`} className="ml-auto h-8 w-8 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
